import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { func } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import css from './PrivacyPolicyCustomPage.module.css';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { fetchPrivacy, getPrivacySelector, fetchPrivacyCcpa, getPrivacyCcpaSelector, fetchServiceAddendums, getServiceAddendumsSelector } from '../../ducks/strapi.duck';

import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom';
import { convertStrapiRichTextToReact, extractPlainTextFromStrapiRichText } from '../../util/richTextHelpers';
import { convertMarkdownToReact } from '../../util/markdownHelpers';
import { Row, Col, Anchor } from 'antd';

function PrivacyPolicyCustomPage(props) {
  const { onUpdateProfile, params, scrollingDisabled } = props;

  const dispatch = useDispatch();
  const privacy = useSelector(getPrivacySelector);
  const privacyCcpa = useSelector(getPrivacyCcpaSelector);
  const serviceAddendums = useSelector(getServiceAddendumsSelector);

  useEffect(() => {
    dispatch(fetchPrivacy({}));
    dispatch(fetchPrivacyCcpa({}));
    dispatch(fetchServiceAddendums({}));
  }, [dispatch]);

  // Get the raw content data
  const rawPrivacyPolicy = privacy?.Content;

  // Extract plain text for meta content
  let privacyPolicyText = '';
  let privacyPolicyContent = null;

  if (Array.isArray(rawPrivacyPolicy)) {
    // It's Strapi rich text format
    privacyPolicyText = extractPlainTextFromStrapiRichText(rawPrivacyPolicy);
    privacyPolicyContent = convertStrapiRichTextToReact(rawPrivacyPolicy);
  } else if (typeof rawPrivacyPolicy === 'string') {
    // It's markdown or plain text
    privacyPolicyText = rawPrivacyPolicy;
    privacyPolicyContent = convertMarkdownToReact(rawPrivacyPolicy);
  }

  console.info('privacyPolicyContent: ', privacyPolicyContent);

  // Get the raw content data
  const rawPrivacyCCPA = privacyCcpa?.Content;

  // Extract plain text for meta content
  let privacyCCPAText = '';
  let privacyCCPAContent = null;

  if (Array.isArray(rawPrivacyCCPA)) {
    // It's Strapi rich text format
    privacyCCPAText = extractPlainTextFromStrapiRichText(rawPrivacyCCPA);
    privacyCCPAContent = convertStrapiRichTextToReact(rawPrivacyCCPA);
  } else if (typeof rawPrivacyCCPA === 'string') {
    // It's markdown or plain text
    privacyCCPAText = rawPrivacyCCPA;
    privacyCCPAContent = convertMarkdownToReact(rawPrivacyCCPA);
  }

  // Get the raw content data
  const rawServicesAddendum = serviceAddendums?.Content;

  // Extract plain text for meta content
  let servicesAddendumText = '';
  let servicesAddendumContent = null;

  if (Array.isArray(rawServicesAddendum)) {
    // It's Strapi rich text format
    servicesAddendumText = extractPlainTextFromStrapiRichText(rawServicesAddendum);
    servicesAddendumContent = convertStrapiRichTextToReact(rawServicesAddendum);
  } else if (typeof rawServicesAddendum === 'string') {
    // It's markdown or plain text
    servicesAddendumText = rawServicesAddendum;
    servicesAddendumContent = convertMarkdownToReact(rawServicesAddendum);
  }

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  // UX: collapsible previews for long sections
  const MAX_PREVIEW_HEIGHT = 420; // px

  const mainRef = useRef(null);
  const ccpaRef = useRef(null);
  const addRef = useRef(null);

  const [mainExpanded, setMainExpanded] = useState(false);
  const [ccpaExpanded, setCcpaExpanded] = useState(false);
  const [addExpanded, setAddExpanded] = useState(false);

  const [mainToggleVisible, setMainToggleVisible] = useState(false);
  const [ccpaToggleVisible, setCcpaToggleVisible] = useState(false);
  const [addToggleVisible, setAddToggleVisible] = useState(false);

  useLayoutEffect(() => {
    const checkHeights = () => {
      if (mainRef.current) setMainToggleVisible(mainRef.current.scrollHeight > MAX_PREVIEW_HEIGHT);
      if (ccpaRef.current) setCcpaToggleVisible(ccpaRef.current.scrollHeight > MAX_PREVIEW_HEIGHT);
      if (addRef.current) setAddToggleVisible(addRef.current.scrollHeight > MAX_PREVIEW_HEIGHT);
    };

    // Run after render
    checkHeights();

    // Also observe resize changes
    const ro = new ResizeObserver(() => checkHeights());
    if (mainRef.current) ro.observe(mainRef.current);
    if (ccpaRef.current) ro.observe(ccpaRef.current);
    if (addRef.current) ro.observe(addRef.current);

    return () => {
      try {
        ro.disconnect();
      } catch (e) {
        /* ignore */
      }
    };
  }, [privacyPolicyContent, privacyCCPAContent, servicesAddendumContent]);

  const scrollToId = id => e => {
    e && e.preventDefault();
    const el = document.getElementById(id);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const redirectToSignupPage = () => {
    history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}));
  };

  return (
    <Page title={'Privacy Policy'} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={
          <TopbarContainer mobileRootClassName={css.mobileTopbar} desktopClassName={css.desktopTopbar} />
        }
        footer={<FooterContainer />}
      >
        <div className={css.container}>
          <h4 className={css.subheading}><span style={{ color: "var(--marketplaceColor)" }}>i</span>parley</h4>
          {/* <h1 className={css.heading}>Privacy <span>Policy</span></h1> */}
          <h1 className={css.heading}>{(privacy?.Heading || privacy?.title || privacy?.attributes?.title) ?? 'Privacy Policy'}</h1>

          {/* <div className={css.content}>
            {privacyPolicyContent || ''}
          </div> */}

          {/* <div className={css.tocWrapper}>
            <nav className={css.toc} aria-label="Privacy page table of contents">
              <ul className={css.tocList}>
                <li className={css.tocItem}><a href="#privacy-main" onClick={scrollToId('privacy-main')}>Privacy Policy</a></li>
                <li className={css.tocItem}><a href="#privacy-ccpa" onClick={scrollToId('privacy-ccpa')}>Privacy Policy — CCPA & CPRA</a></li>
                <li className={css.tocItem}><a href="#privacy-addendums" onClick={scrollToId('privacy-addendums')}>Service Addendums</a></li>
              </ul>
            </nav>
          </div> */}
          <Row>
            <Col xs={24} xl={6} className={css.anchoraffix}>
              <Anchor
                affix={true}
                offsetTop={100}
                items={[
                  {
                    key: 'privacy-main',
                    href: '#privacy-main',
                    title: 'Privacy Policy',
                  },
                  {
                    key: 'privacy-ccpa',
                    href: '#privacy-ccpa',
                    title: 'Privacy Policy — CCPA & CPRA',
                  },
                  {
                    key: 'privacy-addendums',
                    href: '#privacy-addendums',
                    title: 'Service Addendums',
                  },
                ]}
              />
            </Col>
            <Col xs={24} xl={18}>
              <div className={css.privacyContainer}>
                <section id="privacy-main" className={css.privacySection}>
                  <h2 className={css.privacySubheading}>Privacy Policy</h2>
                  <div
                    ref={mainRef}
                    className={`${css.sectionContent} ${mainExpanded ? css.expanded : css.collapsed}`}
                    aria-expanded={mainExpanded}
                  >
                    {privacyPolicyContent || ''}
                  </div>
                  {mainToggleVisible && (
                    <button className={css.expandButton} onClick={() => setMainExpanded(v => !v)}>
                      {mainExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </section>

                <section id="privacy-ccpa" className={css.privacySection}>
                  <h2 className={css.privacySubheading}>Privacy Policy — CCPA & CPRA</h2>
                  <div
                    ref={ccpaRef}
                    className={`${css.sectionContent} ${ccpaExpanded ? css.expanded : css.collapsed}`}
                    aria-expanded={ccpaExpanded}
                  >
                    {privacyCCPAContent || ''}
                  </div>
                  {ccpaToggleVisible && (
                    <button className={css.expandButton} onClick={() => setCcpaExpanded(v => !v)}>
                      {ccpaExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </section>

                <section id="privacy-addendums" className={css.privacySection}>
                  <h2 className={css.privacySubheading}>Service Addendums</h2>
                  <div
                    ref={addRef}
                    className={`${css.sectionContent} ${addExpanded ? css.expanded : css.collapsed}`}
                    aria-expanded={addExpanded}
                  >
                    {servicesAddendumContent || ''}
                  </div>
                  {addToggleVisible && (
                    <button className={css.expandButton} onClick={() => setAddExpanded(v => !v)}>
                      {addExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </section>
              </div>
            </Col>
          </Row>

        </div>
      </LayoutSingleColumn>
    </Page>
  );
}

PrivacyPolicyCustomPage.propTypes = {
  onUpdateProfile: func.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

export default PrivacyPolicyCustomPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(PrivacyPolicyCustomPage);
